import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgIf, NgClass, NgFor } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-company-details-modal',
  standalone: true,
  imports: [NgIf, NgClass, NgFor, MatTooltipModule],
  templateUrl: './company-details-modal.component.html',
  styleUrl: './company-details-modal.component.scss'
})
export class CompanyDetailsModalComponent {
  @Input() isVisible: boolean = false;
  @Input() company: any = null;
  @Output() modalClosed: EventEmitter<void> = new EventEmitter();

  showAllBusinessTags: boolean = false;
  showAllTechnologies: boolean = false;
  showFullDescription: boolean = false;
  selectedDescriptionType: 'extracted' | 'generated' = 'extracted';

  // "View more" sub-modal listing every phone / email.
  contactListOpen: boolean = false;
  contactListType: 'phone' | 'email' = 'phone';
  copiedValue: string | null = null;

  close() {
    this.isVisible = false;
    this.contactListOpen = false;
    this.modalClosed.emit();
  }

  // --- Contact information (Veridion exposes phone_numbers[] and emails[]) ---

  get phoneNumbers(): string[] {
    return this.dedupeContacts([this.company?.primary_phone, ...(this.company?.phone_numbers ?? [])]);
  }

  get emails(): string[] {
    return this.dedupeContacts([this.company?.primary_email, ...(this.company?.emails ?? [])]);
  }

  get primaryPhone(): string {
    return this.phoneNumbers[0] ?? '';
  }

  get primaryEmail(): string {
    return this.emails[0] ?? '';
  }

  get extraPhoneCount(): number {
    return Math.max(0, this.phoneNumbers.length - 1);
  }

  get extraEmailCount(): number {
    return Math.max(0, this.emails.length - 1);
  }

  get contactListItems(): string[] {
    return this.contactListType === 'phone' ? this.phoneNumbers : this.emails;
  }

  get contactListTitle(): string {
    return this.contactListType === 'phone' ? 'Phone numbers' : 'Email addresses';
  }

  openContactList(type: 'phone' | 'email'): void {
    this.contactListType = type;
    this.copiedValue = null;
    this.contactListOpen = true;
  }

  closeContactList(): void {
    this.contactListOpen = false;
  }

  copyContact(value: string): void {
    const markCopied = () => {
      this.copiedValue = value;
      setTimeout(() => {
        if (this.copiedValue === value) this.copiedValue = null;
      }, 1500);
    };

    // navigator.clipboard only exists in secure contexts (https / localhost);
    // fall back to execCommand otherwise so copy works everywhere.
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(markCopied).catch(() => this.fallbackCopy(value, markCopied));
    } else {
      this.fallbackCopy(value, markCopied);
    }
  }

  private fallbackCopy(value: string, onSuccess: () => void): void {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      if (document.execCommand('copy')) onSuccess();
    } catch {
      // clipboard unavailable — leave UI unchanged
    } finally {
      document.body.removeChild(textarea);
    }
  }

  private dedupeContacts(values: any[]): string[] {
    return [...new Set(
      values
        .filter(v => typeof v === 'string' && v.trim() !== '')
        .map(v => v.trim())
    )];
  }

  getLocation(address: any): string {
    if (!address) return 'Location not available';

    const parts = [
      address.city,
      address.region,
      address.country
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(', ') : 'Location not available';
  }

  formatEmployeeCount(count: number | string): string {
    if (!count) return 'Not available';

    const num = typeof count === 'string' ? parseInt(count) : count;
    if (isNaN(num)) return 'Not available';

    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  }

  formatRevenue(revenue: number | string): string {
    if (!revenue) return 'Not available';

    const num = typeof revenue === 'string' ? parseFloat(revenue) : revenue;
    if (isNaN(num)) return 'Not available';

    if (num >= 1000000000) {
      const billions = Math.round(num / 1000000000);
      return billions + 'B';
    } else if (num >= 1000000) {
      const millions = Math.round(num / 1000000);
      return millions + 'M';
    } else if (num >= 1000) {
      const thousands = Math.round(num / 1000);
      return thousands + 'K';
    }
    return Math.round(num).toString();
  }

  getLegalName(legalNames: string[]): string {
    if (!legalNames || !Array.isArray(legalNames) || legalNames.length === 0) {
      return '';
    }
    return legalNames[0];
  }

  hasSocialMedia(company: any): boolean {
    return !!(company?.facebook_url || company?.instagram_url || company?.twitter_url || company?.youtube_url);
  }

  getVisibleBusinessTags(): string[] {
    if (!this.company?.business_tags_generated || !Array.isArray(this.company.business_tags_generated)) {
      return [];
    }

    if (this.showAllBusinessTags || this.company.business_tags_generated.length <= 10) {
      return this.company.business_tags_generated;
    }

    return this.company.business_tags_generated.slice(0, 10);
  }

  getVisibleTechnologies(): string[] {
    if (!this.company?.technologies || !Array.isArray(this.company.technologies)) {
      return [];
    }

    if (this.showAllTechnologies || this.company.technologies.length <= 10) {
      return this.company.technologies;
    }

    return this.company.technologies.slice(0, 10);
  }

  toggleBusinessTags(): void {
    this.showAllBusinessTags = !this.showAllBusinessTags;
  }

  toggleTechnologies(): void {
    this.showAllTechnologies = !this.showAllTechnologies;
  }

  hasExtractedDescription(): boolean {
    return !!this.company?.long_description_extracted;
  }

  hasGeneratedDescription(): boolean {
    return !!this.company?.long_description_generated;
  }

  hasAnyDescription(): boolean {
    return this.hasExtractedDescription() || this.hasGeneratedDescription();
  }

  getActiveDescription(): string {
    if (this.selectedDescriptionType === 'extracted' && this.hasExtractedDescription()) {
      return this.company.long_description_extracted;
    }

    if (this.selectedDescriptionType === 'generated' && this.hasGeneratedDescription()) {
      return this.company.long_description_generated;
    }

    // Fallback: if selected type doesn't exist, return the one that does
    if (this.hasExtractedDescription()) {
      return this.company.long_description_extracted;
    }

    if (this.hasGeneratedDescription()) {
      return this.company.long_description_generated;
    }

    return '';
  }

  selectDescriptionType(type: 'extracted' | 'generated'): void {
    this.selectedDescriptionType = type;
    this.showFullDescription = false; // Reset to collapsed when switching
  }

  getVisibleDescription(): string {
    const description = this.getActiveDescription();
    if (!description) return '';

    if (this.showFullDescription) {
      return description;
    }

    // Show only first 2 lines (approximately 200 characters)
    const lines = description.split('\n');
    if (lines.length <= 2) {
      // If 2 or fewer lines, check character count
      const preview = lines.join('\n');
      if (preview.length <= 200) {
        return preview;
      }
      // Truncate to ~200 characters
      return preview.substring(0, 200);
    }

    return lines.slice(0, 2).join('\n');
  }

  shouldShowDescriptionToggle(): boolean {
    const description = this.getActiveDescription();
    if (!description) return false;

    const lines = description.split('\n');
    return lines.length > 2 || description.length > 200;
  }

  toggleDescription(): void {
    this.showFullDescription = !this.showFullDescription;
  }
}